import React, { Component } from "react";
import styled from "styled-components";
import ThemeSection from "./ThemeSection";
import ColorPalette from "./Color/ColorPalette";
import { PaletteColorType } from "common/constants";
import { Gap20 } from "js/react/components/Gap";
import { ColorChitCollection } from "js/react/views/ThemeEditor/components/Color/ColorChitCollection";
import { _, tinycolor } from "js/vendor";
import { blendColors, trackActivity } from "js/core/utilities/utilities";
import FontControl from "./Font/FontControl";
import { ThemeSpinner } from "./Common/ThemeCommon";
import { Icon, Button, TextField } from "@material-ui/core";
import ThemeOptions from "./ThemeOptions";
import { FlexBox } from "js/react/components/LayoutGrid";
import UploadLogo from "./Footer/UploadLogo";
import { getCurrentFontStyle } from "./ThemeTypography";

const InfoSection = styled.div`
    && h3 {
        text-transform: none;
        font-size: 18px;
    }
    && p,
    && p:last-of-type {
        text-transform: none;
        font-size: 14px;
        margin-bottom: 12px;
        margin-right: 10px;
    }

    && .MuiButtonBase-root {
        font-size: 12px;

        &.MuiButton-contained {
            background-color: #eee;
        }

        .MuiIcon-root {
            font-size: 1.3rem;
        }
    }
`;

const BackgroundOptions = styled(ThemeOptions)`
    .OptionIcon {
        background: #EEEEEF;
    
        .MuiIcon-root {
            width: 40px;
            height: 40px;
        }
    }
`;

const ColorsHeader = styled(FlexBox)`
    margin-bottom: -16px;
`;

const BottomOptions = styled.div`
    padding-left: 28px;
    padding-bottom: 10px;
`;

const ThemeNameContainer = styled.div`
    margin-top: 18px;
    padding-right: 10px;
`;

export default class ThemeSimple extends Component {
    componentDidMount = async () => {
        let {
            fonts,
            fetchFonts,
        } = this.props;

        if (!fonts?.length) {
            await fetchFonts();
        }
    };

    replacePalette = palette => {
        if (!palette.colors.hasOwnProperty("chart1")) {
            let index = 2;
            palette.colors["chart1"] = palette.colors.theme;

            for (let colorName of Object.keys(palette.colors)) {
                if (colorName.startsWith("accent")) {
                    palette.colors["chart" + index] = palette.colors[colorName];
                    index++;
                }
            }
        }
        if (!palette.colors.hasOwnProperty(PaletteColorType.BACKGROUND_ACCENT)) {
            let blendColor = tinycolor(palette.colors[PaletteColorType.THEME]).setAlpha(0.1);
            palette.colors[PaletteColorType.BACKGROUND_ACCENT] = blendColors(blendColor, tinycolor("white")).toRgbString();
        }

        if (!palette.colors.hasOwnProperty(PaletteColorType.POSITIVE)) {
            palette.colors[PaletteColorType.POSITIVE] = "#54C351";
        }
        if (!palette.colors.hasOwnProperty(PaletteColorType.NEGATIVE)) {
            palette.colors[PaletteColorType.NEGATIVE] = "#E04C2B";
        }

        this.props.update(palette);
    };

    handleUpdateColors = colors => {
        let paletteColors = Object.assign({}, this.props.theme.colors);

        // delete all accent colors
        for (let name of Object.keys(paletteColors)) {
            if (name.startsWith("accent")) {
                delete paletteColors[name];
            }
        }

        // add theme and accent colors
        for (let i = 0; i < colors.length; i++) {
            if (i == 0) {
                paletteColors.theme = colors[0];
            } else {
                paletteColors["accent" + i] = colors[i];
            }
        }

        this.props.update({
            colors: paletteColors,
            palette_name: "custom"
        });
    }

    handleNameChange = event => {
        let name = event.target.value;
        this.props.update({
            name,
        });
    }

    handleNameBlur = event => {
        let name = event.target.value.trim();
        if (name !== this.props.theme.name) {
            this.props.update({
                name,
            });
        }
    }

    render() {
        let {
            theme,
            update,
            fonts,
            orgId,
            isSharedTheme,
            hideImportTheme = false,
            fetchFonts,
            changeNavSelection,
            importTheme,
        } = this.props;

        if (!fonts?.length) {
            return <ThemeSpinner />;
        }

        let paletteColors = [];
        let chartColors = [];

        paletteColors.push(theme.colors.theme);
        for (let colorName of Object.keys(theme.colors)) {
            if (colorName.startsWith("accent")) {
                paletteColors.push(theme.colors[colorName]);
            }
            if (colorName.startsWith("chart")) {
                chartColors.push(theme.colors[colorName]);
            }
        }

        let isDarkBackground = theme.defaultBackgroundColor === "background_dark";

        let headerFontStyle = getCurrentFontStyle(theme, fonts, "Header");
        let titleFontStyle = getCurrentFontStyle(theme, fonts, "Title");
        let bodyFontStyle = getCurrentFontStyle(theme, fonts, "Body");

        let allFontsValid = (
            !headerFontStyle.isInvalid &&
            !titleFontStyle.isInvalid &&
            !bodyFontStyle.isInvalid
        );

        return (
            <FlexBox column fillHeight left between>
                <ThemeSection title="Simple">
                    <InfoSection>
                        <h3>Customize your Theme</h3>
                        <p>Tell us a bit about your brand and we'll build a theme for you.</p>
                        {
                            !hideImportTheme &&
                            <Button
                                variant="contained"
                                onClick={importTheme}
                            >
                                <Icon>swap_vert</Icon>
                                <span>Import Theme...</span>
                            </Button>
                        }
                    </InfoSection>

                    <div style={{ clear: "both" }} />

                    {
                        isSharedTheme &&
                        <ThemeNameContainer>
                            <TextField
                                id="themeName"
                                autoFocus={false}
                                onFocus={event => event.target.select()}
                                label="Theme Name"
                                value={theme.name}
                                fullWidth={true}
                                type="text"
                                onChange={this.handleNameChange}
                                onBlur={this.handleNameBlur}
                            />
                        </ThemeNameContainer>
                    }

                    <h4>
                        <ColorsHeader left middle>
                            <span>Colors</span>
                            <ColorPalette
                                collapsed
                                theme={theme}
                                update={this.replacePalette}
                            />
                        </ColorsHeader>
                    </h4>
                    <ColorChitCollection paletteName={theme.palette_name} colors={paletteColors} {...this.props} onUpdate={this.handleUpdateColors} />

                    <div style={{ clear: "both", height: 1 }} />

                    <FontControl
                        simple
                        label="Header Font"
                        style="Header"
                        style2="Title"
                        update={update}
                        theme={theme}
                        fonts={fonts}
                        currentFontStyle={headerFontStyle}
                        refreshFonts={fetchFonts}
                        orgId={orgId}
                        isSharedTheme={isSharedTheme}
                    />
                    <FontControl
                        simple
                        label="Body Font"
                        style="Body"
                        update={update}
                        theme={theme}
                        fonts={fonts}
                        currentFontStyle={bodyFontStyle}
                        refreshFonts={fetchFonts}
                        orgId={orgId}
                        isSharedTheme={isSharedTheme}
                    />

                    <FlexBox left top >
                        <div>
                            <BackgroundOptions
                                options={[
                                    {
                                        value: "background_light",
                                        label: "light",
                                        icon: <Icon>light_mode</Icon>,
                                    },
                                    {
                                        value: "background_dark",
                                        label: "dark",
                                        icon: <Icon>nightlight_round</Icon>,
                                    }
                                ]}
                                title="Background"
                                property="defaultBackgroundColor"
                                update={update}
                                theme={theme}
                            />
                        </div>
                        <Gap20 />
                        <div>
                            <h4>Logo</h4>
                            <UploadLogo
                                uploadButtonIsExternal
                                id="upload-logo-simple"
                                bgColor={isDarkBackground ? theme.colors.background_dark : theme.colors.background_light }
                                src={isDarkBackground ? theme.logo_dark : theme.logo }
                                update={update}
                                attribute="logo"
                                attribute2="logo_dark"
                                onUploadSuccess={logo_id => {
                                    let trackProps = {
                                        logo_changed: true,
                                        logo_dark_changed: true,
                                        source: "theme_editor_simple",
                                        logo_id,
                                    };
                                    if (isSharedTheme) {
                                        trackActivity("OrgTheme", "LogoUpload", null, null, trackProps, { audit: false });
                                    } else {
                                        trackActivity("Theme", "LogoUpload", null, null, trackProps, { audit: false });
                                    }
                                }}
                            />
                        </div>
                    </FlexBox>
                    <Gap20/>
                </ThemeSection>

                <BottomOptions>
                    <Button
                        color="primary"
                        onClick={() => changeNavSelection("colors")}
                        disabled={!allFontsValid}
                    >
                        <Icon>color_lens</Icon>
                        <span>More Options</span>
                    </Button>
                </BottomOptions>
            </FlexBox>
        );
    }
}

